import React from "react";
import { ArrowDownIcon } from "lucide-react";
import { CTAButton } from "../CTAButton";
import { frameworkSteps, reviewQuestions } from "../../data/content";
export function Story() {
  return <>
      <section className="bg-white px-4 py-16 sm:py-24" data-id="element-305" data-mp="21je2eln0mrl0">
        <div className="mx-auto max-w-2xl" data-id="element-306" data-mp="21grtoioe2617">
          <p className="text-center text-sm font-bold uppercase tracking-wide text-gold" data-id="element-307" data-mp="1wl4627pk1kxt">Mastopexy Surgical Blueprint™</p>
          <h2 className="text-balance mt-3 text-center font-serif text-3xl font-bold leading-tight text-ink sm:text-5xl" data-id="element-308" data-mp="18v00vvwpxd4p">
            From studying mastopexy… to understanding the <em className="text-gold" data-id="element-309" data-mp="1ikjkj3s99b0m">complete procedure sequence</em>
          </h2>

          <div className="mt-10 space-y-5 text-[17px] leading-relaxed text-ink/80 sm:text-lg" data-id="element-310" data-mp="1dt1kqj1yxgh8">
            <p data-id="element-311" data-mp="q3pffu9rhsk7">
              I created Mastopexy Surgical Blueprint™ because I noticed something that is rarely addressed clearly in
              surgical education.
            </p>
            <p className="font-semibold text-ink" data-id="element-312" data-mp="hiapxvnhck9e">
              You can watch lectures. You can read textbooks. You can attend conferences. You can study different
              mastopexy techniques.
            </p>
            <p data-id="element-313" data-mp="r7kh99ai1ywg">And still find yourself looking at a new case thinking:</p>
          </div>

          <ul className="mt-6 space-y-3" data-id="element-314" data-mp="w6xfsraqguev">
            {reviewQuestions.map(q => <li key={q} className="rounded-xl border-l-4 border-gold bg-cream px-5 py-4 font-serif text-xl italic leading-snug text-navy" data-id="element-315" data-mp="w906ldjpmrkn">
                “{q}”
              </li>)}
          </ul>

          <div className="mt-8 space-y-5 text-[17px] leading-relaxed text-ink/80 sm:text-lg" data-id="element-316" data-mp="8gt92b290ejn">
            <p className="font-semibold text-ink" data-id="element-317" data-mp="cjzdre9ppxo0">That is exactly why I created this system.</p>
            <p data-id="element-318" data-mp="1jvztxp65s54m">
              Not to replace surgical training. Not to replace mentorship. Not to provide a universal operative recipe.
            </p>
            <p data-id="element-319" data-mp="3kyz9x1pkmywj">
              But to give you a <strong className="text-ink" data-id="element-320" data-mp="3kxxzmzgdqgfv">structured educational blueprint</strong> for reviewing
              mastopexy cases and organizing the procedure from beginning to end.
            </p>
          </div>

          <div className="mt-10" data-id="element-321" data-mp="3tye1k4hh5fim">
            <CTAButton label="I want to review mastopexy with a system" data-id="element-322" data-mp="3txkzhgtgne6t" />
          </div>
        </div>
      </section>

      <section className="bg-navy px-4 py-16 text-white sm:py-24" data-id="element-323" data-mp="31b01hxihc3ua">
        <div className="mx-auto max-w-5xl" data-id="element-324" data-mp="31b9qbhrsg4ye">
          <h2 className="text-balance text-center font-serif text-3xl font-bold leading-tight sm:text-5xl" data-id="element-325" data-mp="2x9y9o1qw6u2c">
            One case. One framework. <em className="text-goldlight" data-id="element-326" data-mp="3b1l744wkm8mp">One complete review in ~15 minutes.</em>
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-center text-[17px] leading-relaxed text-white/75 sm:text-lg" data-id="element-327" data-mp="369bak60yaabg">
            Instead of jumping between lectures, notes, articles and scattered surgical references, you work through
            the case using the same structured sequence every time.
          </p>

          <ol className="mx-auto mt-10 flex max-w-sm flex-col items-stretch lg:max-w-none lg:flex-row lg:items-center lg:gap-2" data-id="element-328" data-mp="t23l78oiqdxd">
            {frameworkSteps.map((step, i) => <li key={step} className="flex flex-col items-center lg:flex-1 lg:flex-row lg:gap-2" data-id="element-329" data-mp="k3zzab9mff51" data-mpi={i}>
                <div className="flex w-full items-center gap-4 rounded-2xl bg-navy2 px-5 py-4 ring-1 ring-white/10 lg:flex-col lg:gap-2 lg:px-3 lg:py-5" data-id="element-330" data-mp="p2kzwbssc0u0">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-goldlight font-serif text-lg font-bold text-navy" data-id="element-331" data-mp="1gszbu2h8n331">
                    {i + 1}
                  </span>
                  <span className="text-lg font-extrabold uppercase tracking-wider" data-id="element-332" data-mp="17s1356vuu7e4">{step}</span>
                </div>
                {i < frameworkSteps.length - 1 && <ArrowDownIcon className="my-1.5 h-5 w-5 text-goldlight lg:hidden" aria-hidden="true" data-id="element-333" data-mp="3nli3m8q6q40p" />}
              </li>)}
          </ol>

          <div className="mx-auto mt-10 max-w-2xl space-y-3 text-center text-[17px] leading-relaxed text-white/80 sm:text-lg" data-id="element-334" data-mp="boc2z3sgd9nz">
            <p data-id="element-335" data-mp="6ty6sz5yuyi9">The goal isn't to replace your clinical judgment.</p>
            <p className="font-semibold text-white" data-id="element-336" data-mp="2v8qaxpmt0ue">
              The goal is to give you a repeatable educational framework for studying mastopexy in a more organized way.
            </p>
          </div>

          <div className="mt-10" data-id="element-337" data-mp="ul3jj5gm1aoc">
            <CTAButton label="I want this 6-step framework" tone="dark" data-id="element-338" data-mp="qk1tnf8l2az1" />
          </div>
        </div>
      </section>
    </>;
}